import React, { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Check, Clock, X } from "lucide-react";
import { snoozeTask } from "@/lib/push";
import { toast } from "sonner";
import { formatTime, nagSourceIsos } from "@/utils";

const OPTIONS = [
    { label: "5 min", mins: 5 },
    { label: "15 min", mins: 15 },
    { label: "30 min", mins: 30 },
    { label: "1 hour", mins: 60 },
    { label: "3 hours", mins: 180 },
    { label: "Tomorrow 9am", tomorrow: true },
    { label: "Next week", mins: 60 * 24 * 7 },
    { label: "Custom", custom: true },
];

export const NagOverlay = ({ task, open, onClose, onComplete, onSnoozed }) => {
    const [showCustom, setShowCustom] = useState(false);
    const [customDate, setCustomDate] = useState("");
    const [customTime, setCustomTime] = useState("09:00");

    if (!task) return null;

    // Recurring nags are occurrence-specific: act on the exact SOURCE key the push carried
    // (`_occKey`), else the most recent outstanding one, else today's — never the whole series.
    const nagIsos = () => nagSourceIsos(task);

    const doSnooze = async (opt) => {
        try {
            let mins = null, until = null;
            if (opt.tomorrow) {
                const d = new Date();
                d.setDate(d.getDate() + 1);
                d.setHours(9, 0, 0, 0);
                until = d.getTime();
            } else if (opt.custom) {
                setShowCustom(true);
                return;
            } else {
                mins = opt.mins;
            }
            await snoozeTask(task.id, mins, until, nagIsos());
            toast.success(`Snoozed · ${opt.label}`);
            onSnoozed?.();
            onClose?.();
        } catch (e) {
            toast.error("Snooze failed");
        }
    };

    const submitCustom = async (e) => {
        e.preventDefault();
        if (!customDate) return;
        const [y, m, d] = customDate.split("-").map(Number);
        const [hh, mm] = customTime.split(":").map(Number);
        const until = new Date(y, m - 1, d, hh, mm, 0, 0).getTime();
        if (until <= Date.now()) return toast.error("Pick a future time");
        try {
            await snoozeTask(task.id, null, until, nagIsos());
            toast.success("Snoozed");
            onSnoozed?.();
            onClose?.();
        } catch {
            toast.error("Snooze failed");
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="bg-meye-card border border-meye-fuchsia/40 max-w-sm text-zinc-100 p-0 gap-0" data-testid="nag-overlay">
                <DialogTitle className="sr-only">Task Due</DialogTitle>
                <div className="px-4 py-3 border-b border-meye-border bg-meye-fuchsia/10">
                    <p className="text-[10px] uppercase tracking-widest text-meye-fuchsia font-mono flex items-center gap-1.5">
                        <Clock size={11} strokeWidth={2.25} /> task due{task.time ? ` · ${formatTime(task.time)}` : ""}
                    </p>
                </div>
                <div className="p-4">
                    <h3 className="font-brand text-2xl font-bold text-zinc-100 leading-tight mb-4" data-testid="nag-task-text">
                        {task.text}
                    </h3>

                    {!showCustom ? (
                        <>
                            <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-2">nag me again in…</p>
                            <div className="grid grid-cols-2 gap-2">
                                {OPTIONS.map((o) => (
                                    <button
                                        key={o.label}
                                        onClick={() => doSnooze(o)}
                                        className="text-xs font-brand font-bold py-2.5 rounded-md border border-meye-border bg-black/40 text-zinc-200 hover:border-meye-fuchsia hover:text-meye-fuchsia transition-colors"
                                        data-testid={`nag-snooze-${o.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                    >
                                        {o.label}
                                    </button>
                                ))}
                            </div>
                        </>
                    ) : (
                        <form onSubmit={submitCustom} className="space-y-3">
                            <div className="grid grid-cols-2 gap-2">
                                <input
                                    type="date"
                                    value={customDate}
                                    onChange={(e) => setCustomDate(e.target.value)}
                                    className="bg-black/40 border border-meye-border rounded p-2 text-sm text-zinc-100"
                                    data-testid="nag-custom-date"
                                    autoFocus
                                    required
                                />
                                <input
                                    type="time"
                                    value={customTime}
                                    onChange={(e) => setCustomTime(e.target.value)}
                                    className="bg-black/40 border border-meye-border rounded p-2 text-sm text-zinc-100"
                                    data-testid="nag-custom-time"
                                />
                            </div>
                            <div className="flex justify-end gap-2">
                                <button
                                    type="button"
                                    onClick={() => setShowCustom(false)}
                                    className="text-xs text-zinc-400 hover:text-white px-3 py-2"
                                >
                                    back
                                </button>
                                <button
                                    type="submit"
                                    className="text-xs bg-meye-fuchsia text-black font-brand uppercase tracking-widest px-4 py-2 rounded"
                                    data-testid="nag-custom-submit"
                                >
                                    snooze
                                </button>
                            </div>
                        </form>
                    )}

                    <div className="mt-4 flex gap-2 pt-3 border-t border-meye-border">
                        <button
                            onClick={onClose}
                            className="flex-1 text-xs text-zinc-400 hover:text-white py-2 flex items-center justify-center gap-1"
                            data-testid="nag-dismiss"
                        >
                            <X size={12} /> dismiss
                        </button>
                        <button
                            onClick={() => onComplete?.(nagIsos()?.[0] || null)}
                            className="flex-1 text-xs bg-emerald-500 hover:bg-emerald-400 text-black font-brand uppercase tracking-widest py-2 rounded flex items-center justify-center gap-1.5"
                            data-testid="nag-complete"
                        >
                            <Check size={12} strokeWidth={3} /> mark done
                        </button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default NagOverlay;
